import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, VerifiedUser } from '@mui/icons-material';
import { AdfsItemFragment, PortalLoginItemFragment } from '../__generated__/graphql';
import { DELETE_CLIENT_AUTHORIZATION, GET_SINGLE_ADFS_BY_BKS_ID } from '../administration/ADFSConfig/ADFSConfig.graphql';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingSpinner from '../components/LoadingSpinner';
import { GET_PORTAL_LOGIN, SET_PORTAL_LOGIN_GROUP } from './Sso.graphql';
import { SsoSection } from './SsoSection';
import CopyButton from './CopyButton';
import CustomClientRuleDialog from './CustomClientRuleDialog';

interface Props {
  adfs: AdfsItemFragment;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Rule = NonNullable<NonNullable<PortalLoginItemFragment['otherRules']>[number]>;

// Identity falls back to this wherever a client has no rule of its own.
const ANY_CLIENT = '*';
// The client every customer's group was written for before ANY_CLIENT existed.
const OLD_PORTAL_CLIENT = 'byggforsk.no';

const singleGroup = (rule: Rule) =>
  rule.requiredClaims?.length === 1 && rule.requiredClaims[0]?.type === 'groups' ? (rule.requiredClaims[0]?.value ?? null) : null;

function RulesTable({ rules, onDelete }: { rules: Rule[]; onDelete?: (clientId: string) => void }) {
  return (
    <Table size="small" sx={{ maxWidth: 720 }}>
      <TableHead>
        <TableRow>
          <TableCell>Klient</TableCell>
          <TableCell>Krever</TableCell>
          {onDelete && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rules.map((rule) => (
          <TableRow key={rule?.clientAuthorizationID}>
            <TableCell>
              {rule.clientAuthorizationID === ANY_CLIENT ? (
                <Chip size="small" variant="outlined" label="Alle klienter" />
              ) : (
                rule.clientAuthorizationID
              )}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace' }}>
              {(rule?.requiredClaims ?? []).map((claim) => `${claim?.type} = ${claim?.value}`).join(' og ')}
            </TableCell>
            {onDelete && (
              <TableCell align="right" sx={{ width: 48 }}>
                <IconButton
                  size="small"
                  aria-label={`Slett regelen for ${rule?.clientAuthorizationID}`}
                  onClick={() => onDelete(rule?.clientAuthorizationID ?? '')}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Written as a rule for every client: the portal signs in through several, and later ones are covered too. */
export default function SsoClientAuthorizationSection({ adfs }: Props) {
  const bksID = adfs.bksID;
  const [editing, setEditing] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetchQueries = [
    { query: GET_PORTAL_LOGIN, variables: { bksID } },
    { query: GET_SINGLE_ADFS_BY_BKS_ID, variables: { bksID } },
  ];

  const { data, loading } = useQuery(GET_PORTAL_LOGIN, { variables: { bksID }, fetchPolicy: 'cache-and-network' });
  const [setGroup, { loading: saving }] = useMutation(SET_PORTAL_LOGIN_GROUP, { refetchQueries, awaitRefetchQueries: true });
  const [remove] = useMutation(DELETE_CLIENT_AUTHORIZATION, { refetchQueries, awaitRefetchQueries: true });

  const login = data?.portalLogin;
  const scheme = login?.scheme ?? adfs.scheme ?? '';
  const otherRules = (login?.otherRules ?? []).filter((x): x is Rule => !!x);
  // Not a single group, so the choice below cannot show it.
  const customEveryClient = otherRules.some((x) => x.clientAuthorizationID === ANY_CLIENT);
  // A group still written for the old portal alone, until Script015 has moved it.
  const oldPortalRule = otherRules.find((x) => x.clientAuthorizationID === OLD_PORTAL_CLIENT);
  const oldPortalGroup = !login?.groupId && !customEveryClient && oldPortalRule ? singleGroup(oldPortalRule) : null;

  const startEditing = () => {
    const current = login?.groupId ?? oldPortalGroup;
    setRestricted(!!current);
    setGroupId(current ?? '');
    setShowCustom(otherRules.length > 0);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setError(null);
    try {
      await setGroup({ variables: { input: { bksID, groupId: restricted ? groupId.trim() : null } } });
      setEditing(false);
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke lagre.');
    }
  };

  const trimmed = groupId.trim();
  const valid = !restricted || trimmed.length > 0;

  return (
    <SsoSection
      title="Klientautorisasjon"
      icon={<VerifiedUser fontSize="small" />}
      action={
        !editing &&
        login && (
          <Button size="small" variant="outlined" onClick={startEditing}>
            Rediger
          </Button>
        )
      }
    >
      {loading && !login && <LoadingSpinner />}

      {login && !editing && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Hvem får logge inn
            </Typography>
            {customEveryClient ? (
              <Typography variant="body2">En egen regel for alle klienter, se under</Typography>
            ) : login.groupId ? (
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">Bare medlemmer av gruppen</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {login.groupId}
                </Typography>
                <CopyButton value={login.groupId} label="Kopier gruppe-ID" />
              </Stack>
            ) : (
              <Typography variant="body2">Alle brukere som logger inn via {scheme}</Typography>
            )}
          </Box>

          {oldPortalGroup && (
            <Alert severity="warning">
              Gruppen {oldPortalGroup} er bare satt for den gamle portalen ({OLD_PORTAL_CLIENT}). Gjennom den nye portalen og de andre klientene
              slipper alle brukere fra {scheme} inn. Rediger og lagre for å la gruppen gjelde overalt.
            </Alert>
          )}

          {otherRules.length > 0 && (
            <Box>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
                Egne regler per klient
              </Typography>
              <RulesTable rules={otherRules} />
            </Box>
          )}
        </Stack>
      )}

      {login && editing && (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Hvem får logge inn
            </Typography>
            <RadioGroup value={restricted ? 'group' : 'all'} onChange={(e) => setRestricted(e.target.value === 'group')}>
              <FormControlLabel value="all" control={<Radio size="small" />} label={`Alle brukere som logger inn via ${scheme}`} />
              <FormControlLabel value="group" control={<Radio size="small" />} label="Bare medlemmer av en gruppe i kundens Entra ID" />
            </RadioGroup>
          </Box>

          {restricted && (
            <TextField
              label="Gruppe-ID"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              required
              fullWidth
              sx={{ maxWidth: 520 }}
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              helperText={
                trimmed && !GUID.test(trimmed)
                  ? 'Ser ikke ut som en objekt-ID. Entra sender gruppens objekt-ID med mindre kunden har valgt noe annet.'
                  : 'Gruppens objekt-ID i Entra. Kunden må sende groups-claimet: Token configuration → Add groups claim i app-registreringen.'
              }
            />
          )}

          <Typography variant="body2" color="text.secondary">
            Gjelder portalen og alle andre tjenester vi logger inn til, med mindre en klient har en egen regel.
          </Typography>

          {restricted && oldPortalGroup && (
            <Alert severity="warning">
              Gruppen er hentet fra regelen for den gamle portalen. Når du lagrer, gjelder den overalt, og brukere fra {scheme} som ikke er med i
              gruppen, slipper ikke inn i den nye portalen lenger.
            </Alert>
          )}
          {customEveryClient && <Alert severity="info">Når du lagrer, erstattes den egne regelen for alle klienter av valget over.</Alert>}

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={!valid || saving} onClick={save}>
              Lagre
            </Button>
            <Button disabled={saving} onClick={() => setEditing(false)}>
              Avbryt
            </Button>
          </Stack>

          {!showCustom ? (
            <Box>
              <Button size="small" onClick={() => setShowCustom(true)}>
                Egne regler per klient …
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" component="h3">
                Egne regler per klient
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                For enkeltklienter som skal ha en annen regel enn den over, som SINTEFs egne apper. En klients egen regel gjelder i stedet for den
                over. Legges til og slettes med en gang.
              </Typography>
              {otherRules.length > 0 && <RulesTable rules={otherRules} onDelete={setDeleting} />}
              <Button size="small" variant="outlined" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setAdding(true)}>
                Legg til regel
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {adding && <CustomClientRuleDialog adfsId={adfs.id} scheme={scheme} bksID={bksID} onClose={() => setAdding(false)} />}
      <ConfirmDialog
        open={deleting !== null}
        title="Slette regelen?"
        message={
          login?.groupId
            ? `${deleting} følger da regelen over: bare medlemmer av gruppen slipper inn.`
            : `Alle brukere fra ${scheme} slipper inn i ${deleting} når regelen er borte.`
        }
        confirmText="Slett"
        deny={() => setDeleting(null)}
        confirm={async () => {
          const clientId = deleting;
          setDeleting(null);
          await remove({ variables: { input: { id: adfs.id, name: clientId, scheme } } });
        }}
      />
    </SsoSection>
  );
}
